import { useEffect, useRef, useState } from 'react'
import {
  Bot,
  Check,
  Circle,
  CornerDownLeft,
  Pause,
  Play,
  RotateCcw,
  Sparkles,
  Square,
  X,
} from 'lucide-react'
import type { AgentControls } from '../agent/useAgentRun'
import { agentScenarios } from '../agent/scenarios'
import { isAgentActive } from '../agent/machine'

interface AgentPanelProps {
  agent: AgentControls
  onClose: () => void
}

const phaseLabel = {
  idle: 'Ready',
  planning: 'Planning',
  streaming: 'Streaming',
  applying: 'Applying patch',
  verifying: 'Verifying',
  paused: 'Paused',
  completed: 'Complete',
  cancelled: 'Cancelled',
  error: 'Needs attention',
}

export function AgentPanel({ agent, onClose }: AgentPanelProps) {
  const [prompt, setPrompt] = useState(agentScenarios[0].prompt)
  const transcriptRef = useRef<HTMLDivElement>(null)
  const { state } = agent
  const active = isAgentActive(state.phase)

  useEffect(() => {
    const element = transcriptRef.current
    if (element) element.scrollTop = element.scrollHeight
  }, [state.transcript])

  return (
    <section className="agent-panel" aria-label="AI coding agent">
      <div className="agent-heading">
        <div className="agent-title">
          <span className="agent-mark"><Sparkles size={14} /></span>
          <div><strong>Driftless Agent</strong><span>Local deterministic demo</span></div>
        </div>
        <div className={`agent-phase phase-${state.phase}`}>
          <i /> {phaseLabel[state.phase]}
        </div>
        <button className="panel-close" type="button" aria-label="Close agent panel" onClick={onClose}>
          <X size={14} />
        </button>
      </div>

      <div className="agent-body">
        <div className="agent-compose">
          <label htmlFor="agent-prompt">Ask the workspace</label>
          <div className="prompt-box">
            <textarea
              id="agent-prompt"
              value={prompt}
              rows={3}
              placeholder="Describe a design or code change…"
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault()
                  void agent.run(prompt)
                }
              }}
            />
            <button
              type="button"
              aria-label="Run agent"
              disabled={!prompt.trim() || active}
              onClick={() => void agent.run(prompt)}
            >
              <CornerDownLeft size={13} />
            </button>
          </div>
          <div className="scenario-list">
            {agentScenarios.map((scenario) => (
              <button key={scenario.id} type="button" disabled={active} onClick={() => setPrompt(scenario.prompt)}>
                {scenario.label}
              </button>
            ))}
          </div>
          <div className="agent-safety">
            <Bot size={13} />
            <p><strong>Scoped execution</strong><span>Graph patches only · no network</span></p>
          </div>
        </div>

        <div className="agent-transcript" ref={transcriptRef} aria-live="polite">
          {state.transcript ? (
            <p>{state.transcript}<span className={active && state.phase !== 'paused' ? 'stream-caret' : ''} /></p>
          ) : (
            <div className="agent-empty">
              <Sparkles size={18} />
              <strong>Intent becomes a reversible patch.</strong>
              <span>Try a prompt to watch token streaming, atomic graph updates, sandbox verification, and branch history work together.</span>
            </div>
          )}
          {state.error ? <div className="agent-error">{state.error}</div> : null}
        </div>

        <div className="agent-steps">
          <div className="steps-heading"><span>Run trace</span>{state.firstTokenMs ? <small>TTFT {Math.round(state.firstTokenMs)} ms</small> : null}</div>
          {state.steps.length > 0 ? (
            <ol>
              {state.steps.map((step) => (
                <li className={`step-${step.status}`} key={step.id}>
                  <span className="step-icon">
                    {step.status === 'done' ? <Check size={10} /> : step.status === 'running' ? <span className="step-spinner" /> : <Circle size={8} />}
                  </span>
                  <span>{step.label}</span>
                </li>
              ))}
            </ol>
          ) : (
            <div className="trace-placeholder"><i /><i /><i /><i /></div>
          )}
          <div className="agent-controls">
            {active ? (
              <>
                {state.phase === 'paused' ? (
                  <button type="button" onClick={agent.resume}><Play size={12} /> Resume</button>
                ) : (
                  <button type="button" onClick={agent.pause}><Pause size={12} /> Pause</button>
                )}
                <button type="button" onClick={agent.cancel}><Square size={10} /> Stop</button>
              </>
            ) : state.phase === 'completed' || state.phase === 'cancelled' || state.phase === 'error' ? (
              <>
                <button type="button" onClick={agent.retry}><RotateCcw size={12} /> Retry</button>
                <button type="button" onClick={agent.reset}>Clear</button>
              </>
            ) : (
              <span><kbd>⌘</kbd><kbd>↵</kbd> to run</span>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
